// Addendum to Form QC6 / QC7 — the field spec, shared by the workflow pages,
// the Management "Add addendum" form, the server's validation and the PDF
// (CommonJS for the same reason as qcQuestions.js).
//
// Source: the firm's "QC6_QC7_Addendum_Template.docx". An addendum documents
// information identified AFTER the original QC6 / QC7 was approved; it is read
// together with the original form and never edits its answers.

const { RISK_OPTIONS, qcAnswerForEngagement } = require('./qcQuestions.js');
const { engagementTag } = require('../utils/engagementName');

const DEFAULT_FIRM_NAME = 'PKF-CAP LLP';

// Nature of Update / Change — any combination (the Word form has checkboxes).
const NATURE_OPTIONS = [
  ['risk', 'Change in engagement risk assessment'],
  ['independence', 'Independence / conflict update'],
  ['other', 'Other (please specify)'],
];

// Impact Assessment — Independence / Ethical Considerations (pick one).
const IMPACT_OPTIONS = [
  ['no_new', 'No new threats identified'],
  ['safeguarded', 'Threats identified but adequately safeguarded'],
  ['significant', 'Significant threats identified (details documented above)'],
];

const PURPOSE_TEXT = 'This addendum is prepared to document information identified after the initial completion and approval of Form QC6 / QC7. It forms part of the engagement acceptance or continuance documentation and should be read in conjunction with the original QC6 / QC7 form.';
const DETAILS_HINT = 'Provide details of the information received or changes identified, the reasons such information was not available at the time of the original QC6 / QC7 approval, and the evaluation performed by the engagement team.';
const CONCLUSION_TEXT = 'Based on the updated information, the acceptance / continuance decision remains appropriate.';

const titleFor = (qcType) => `Addendum to Form ${String(qcType || 'QC6 / QC7').toUpperCase()}`;
const natureLabel = (code) => (NATURE_OPTIONS.find(([c]) => c === code) || [])[1] || code;
const impactLabel = (code) => (IMPACT_OPTIONS.find(([c]) => c === code) || [])[1] || code || '—';

/** The risk rating the original form concluded with. */
const originalRiskOf = (record) => {
  if (!record || !record.answers) return null;
  const p = String(record.qcType || '').toUpperCase() === 'QC7' ? 'q7' : 'q6';
  const v = record.answers[`${p}_c_rating`];
  return v === undefined || v === null || String(v).trim() === '' ? null : String(v);
};

/** One covered engagement's rating on the original form (its own when rated per engagement). */
const originalRiskForEngagement = (record, engagementId) => {
  if (!record || !record.answers) return null;
  const p = String(record.qcType || '').toUpperCase() === 'QC7' ? 'q7' : 'q6';
  const v = qcAnswerForEngagement(record.answers, `${p}_c_rating`, engagementId);
  return v === undefined || v === null || String(v).trim() === '' ? null : String(v);
};

/** The highest of some ratings (null when none is a rating). */
const highestRisk = (ratings) => (ratings || [])
  .filter((r) => RISK_OPTIONS.includes(r))
  .reduce((m, r) => (m === null || RISK_OPTIONS.indexOf(r) > RISK_OPTIONS.indexOf(m) ? r : m), null);

/** The EQR the original form named (null when blank). */
const originalEqrOf = (record) => {
  if (!record || !record.answers) return null;
  const p = String(record.qcType || '').toUpperCase() === 'QC7' ? 'q7' : 'q6';
  const v = record.answers[`${p}_c_eqr`];
  return v === undefined || v === null || String(v).trim() === '' ? null : String(v).trim();
};

/** How an EQR value prints — a cleared assignment reads "No reviewer assigned". */
const NO_EQR_LABEL = 'No reviewer assigned';
const eqrLabel = (v) => (v === undefined || v === null || String(v).trim() === '' ? NO_EQR_LABEL : String(v));

const emptyAddendumForm = () => ({
  firmName: DEFAULT_FIRM_NAME,
  nature: [],
  natureOther: '',
  details: '',
  riskChanged: '',          // 'unchanged' | 'revised' | ''
  riskTo: '',
  // A record covering several engagements revises them one by one:
  // { <engagementId>: { to, reason } } — only the engagements that move.
  riskChanges: {},
  // The EQR after the addendum — only asked when the rating is revised;
  // prefilled with the current reviewer, may be changed or cleared ('').
  eqrTo: '',
  independenceImpact: '',
  reviewerStaffId: '',
  additionalReviewerStaffId: '',
  thirdReviewerStaffId: '',
});

// ── Per-engagement revision (a record covering several engagements) ───────
// The record's current rating per engagement comes from the server
// (qcAddendaView.currentOf → risk.byEngagement). The addendum revises the
// engagements that moved, each with its own reason; the others keep theirs.

/** "Company — Service #n · description" (engagementTag) for one covered engagement. */
const engagementLabelOf = (b) => {
  if (!b) return '';
  const service = engagementTag(b.serviceProvided, b.engagementSeq, b.serviceDescription);
  if (b.entityName) return service ? `${b.entityName} — ${service}` : b.entityName;
  return b.engagementName || `Engagement #${b.engagementId}`;
};

/** Does this record revise its rating engagement by engagement? */
const isPerEngagementRisk = (byEngagement) => Array.isArray(byEngagement) && byEngagement.length > 1;

/** The form's { <engagementId>: { to, reason } } from a body value (object or its JSON). */
const parseRiskChanges = (raw) => {
  let v = raw;
  if (typeof v === 'string') { try { v = v.trim() ? JSON.parse(v) : {}; } catch { v = {}; } }
  if (Array.isArray(v)) v = Object.fromEntries(v.map((c) => [String(c.engagementId), { to: c.to, reason: c.reason }]));
  return v && typeof v === 'object' ? v : {};
};

/** The engagements the form picked a revised rating for: [[engagementId, { to, reason }]]. */
const pickedRiskChanges = (f, byEngagement) => {
  const ids = new Set((byEngagement || []).map((b) => String(b.engagementId)));
  return Object.entries(f.riskChanges || {})
    .filter(([eid, c]) => ids.has(String(eid)) && c && RISK_OPTIONS.includes(c.to));
};

/**
 * The stored revisions [{ engagementId, engagementName, from, to, reason }]
 * — `from` is the engagement's CURRENT rating (locked), in coverage order.
 */
const riskChangesFrom = (f, byEngagement) => {
  const picked = new Map(pickedRiskChanges(f, byEngagement).map(([eid, c]) => [String(eid), c]));
  return (byEngagement || [])
    .filter((b) => picked.has(String(b.engagementId)))
    .map((b) => {
      const c = picked.get(String(b.engagementId));
      return {
        engagementId: b.engagementId, engagementName: engagementLabelOf(b),
        from: b.rating || null, to: c.to, reason: String(c.reason || '').trim(),
      };
    });
};

/** Every engagement's rating once the revisions apply. */
const ratingsAfter = (byEngagement, changes) => (byEngagement || []).map((b) => {
  const c = (changes || []).find((x) => String(x.engagementId) === String(b.engagementId));
  return c ? c.to : b.rating;
});

/** The revisions as one line ("Company: Low → High (reason); …"), null when none. */
const riskChangesText = (changes) => ((Array.isArray(changes) ? changes : [])
  .map((c) => `${c.engagementName || `Engagement #${c.engagementId}`}: ${c.from || 'Unrated'} → ${c.to}${c.reason ? ` (${c.reason})` : ''}`)
  .join('; ')) || null;

/**
 * An addendum's rating fields as filed: { riskChanged, riskFrom, riskTo,
 * riskChanges }. `current` = currentOf(record).risk. Per engagement, riskFrom
 * / riskTo summarise the record as a whole (the highest rating before /
 * after); with one engagement, riskTo is the form's "to".
 */
const addendumRiskFieldsOf = (f, current) => {
  const revised = f.riskChanged === 'revised';
  const byEngagement = (current && current.byEngagement) || [];
  const riskFrom = (current && current.rating) || null;
  if (!revised) return { riskChanged: false, riskFrom, riskTo: null, riskChanges: [] };
  if (!isPerEngagementRisk(byEngagement)) return { riskChanged: true, riskFrom, riskTo: f.riskTo || null, riskChanges: [] };
  const riskChanges = riskChangesFrom(f, byEngagement);
  return { riskChanged: true, riskFrom, riskTo: highestRisk(ratingsAfter(byEngagement, riskChanges)), riskChanges };
};

/**
 * Required-field check → the blanks as `[{ key, label }]` (the server
 * re-validates). `key` is the `field=` key of the QcadForm control that is
 * empty — the same key the return-comment layer uses — so a page can hand the
 * list to formValidation and mark the very controls that are empty; `label`
 * is the banner wording. `needsQmp` = QC7 parent with a revised rating.
 *
 * The "please specify" box and the "to" rating select sit INSIDE the Nature
 * and Risk Rating controls, so their gaps mark those controls.
 */
const addendumRequiredGaps = (f, { needsQmp = false, byEngagement = null } = {}) => {
  const gaps = [];
  const gap = (key, label) => gaps.push({ key, label });
  if (!String(f.firmName || '').trim()) gap('firmName', "Firm's Name");
  if (!Array.isArray(f.nature) || f.nature.length === 0) gap('nature', 'Nature of Update / Change');
  if (Array.isArray(f.nature) && f.nature.includes('other') && !String(f.natureOther || '').trim()) gap('nature', 'Other — please specify');
  if (!String(f.details || '').trim()) gap('details', 'Details of Update');
  if (f.riskChanged !== 'unchanged' && f.riskChanged !== 'revised') gap('riskChanged', 'Risk Rating (Unchanged / Revised)');
  if (f.riskChanged === 'revised') {
    if (isPerEngagementRisk(byEngagement)) {
      const picked = pickedRiskChanges(f, byEngagement);
      if (picked.length === 0) gap('riskChanged', 'Revised risk rating (at least one engagement)');
      picked.forEach(([eid, c]) => {
        if (!String(c.reason || '').trim()) {
          gap('riskChanged', `Reason for revising ${engagementLabelOf(byEngagement.find((b) => String(b.engagementId) === String(eid)))}`);
        }
      });
    } else if (!RISK_OPTIONS.includes(f.riskTo)) gap('riskChanged', 'Revised risk rating (to)');
  }
  if (!IMPACT_OPTIONS.some(([c]) => c === f.independenceImpact)) gap('independenceImpact', 'Independence / Ethical Considerations');
  if (needsQmp && !f.additionalReviewerStaffId) gap('additionalReviewerName', 'Quality Management Partner (approves the revised rating)');
  return gaps;
};

/**
 * Business rules on filled-in values (not blanks) → messages. `riskFrom` is
 * the record's current rating (locked): a revised rating must move away from it.
 */
const addendumRuleProblems = (f, { riskFrom = null, byEngagement = null } = {}) => {
  const problems = [];
  if (f.riskChanged === 'revised' && isPerEngagementRisk(byEngagement)) {
    pickedRiskChanges(f, byEngagement).forEach(([eid, c]) => {
      const b = byEngagement.find((x) => String(x.engagementId) === String(eid));
      if (b && b.rating && c.to === b.rating) problems.push(`Revised risk rating for ${engagementLabelOf(b)} must differ from its current rating`);
    });
  } else if (f.riskChanged === 'revised' && RISK_OPTIONS.includes(f.riskTo) && riskFrom && f.riskTo === riskFrom) {
    problems.push('Revised risk rating must differ from the current rating');
  }
  // A revised rating IS a change in the risk assessment — the Nature box
  // must say so, or the addendum contradicts itself.
  if (f.riskChanged === 'revised' && !(Array.isArray(f.nature) && f.nature.includes('risk'))) {
    problems.push(`Tick "${natureLabel('risk')}" under Nature of Update / Change when the risk rating is revised`);
  }
  return problems;
};

/**
 * The EQR part of an addendum body (server side, both channels): asked only
 * when the rating is revised, optional — the rating does not make it
 * mandatory — and prefilled with the current reviewer. `eqrTo` absent from
 * the body ⇒ the reviewer stays; present and blank ⇒ the assignment is
 * cleared. `eqrFrom` is the record's current reviewer, locked like riskFrom.
 */
const addendumEqrFieldsOf = (body, revised, currentEqr) => {
  const from = currentEqr || null;
  if (!revised || body.eqrTo === undefined) return { eqrChanged: false, eqrFrom: from, eqrTo: from };
  const to = String(body.eqrTo || '').trim() || null;
  return { eqrChanged: to !== from, eqrFrom: from, eqrTo: to };
};

/**
 * Blanks and rule breaches as one list of labels — the server's 400 message
 * and any banner that only names the problems.
 */
const missingAddendumFields = (f, opts = {}) => [
  ...addendumRequiredGaps(f, opts).map((g) => g.label),
  ...addendumRuleProblems(f, opts),
];

module.exports = {
  DEFAULT_FIRM_NAME,
  NATURE_OPTIONS,
  IMPACT_OPTIONS,
  PURPOSE_TEXT,
  DETAILS_HINT,
  CONCLUSION_TEXT,
  RISK_OPTIONS,
  titleFor,
  natureLabel,
  impactLabel,
  originalRiskOf,
  originalRiskForEngagement,
  highestRisk,
  engagementLabelOf,
  isPerEngagementRisk,
  parseRiskChanges,
  pickedRiskChanges,
  riskChangesFrom,
  ratingsAfter,
  riskChangesText,
  addendumRiskFieldsOf,
  originalEqrOf,
  eqrLabel,
  NO_EQR_LABEL,
  emptyAddendumForm,
  addendumRequiredGaps,
  addendumRuleProblems,
  addendumEqrFieldsOf,
  missingAddendumFields,
};
